
import React, { useState } from 'react';
import { ref, push, set, remove, update, get, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../../firebase';
import { useApp } from '../../App';
import { Category } from '../../types';
import { Plus, Trash2, X, Check, Edit2, Eye, EyeOff, Info, Loader2 } from 'lucide-react';

const AdminCategories: React.FC = () => {
  const { categories } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [originalSlug, setOriginalSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({ 
    name: '', 
    slug: '', 
    icon: '',
    isVisibleOnHome: true 
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingId) {
        // 1. Kategori bilgilerini güncelle
        await update(ref(db, `categories/${editingId}`), formData);

        // 2. Eğer slug değiştiyse, bu kategoriye ait TÜM ürünleri bul ve güncelle
        if (originalSlug && originalSlug !== formData.slug) {
          const productsRef = ref(db, 'products');
          // Bu kategorideki ürünleri bul
          const productsQuery = query(productsRef, orderByChild('category'), equalTo(originalSlug));
          const snapshot = await get(productsQuery);

          if (snapshot.exists()) {
            const updates: any = {};
            snapshot.forEach((child) => {
              updates[`products/${child.key}/category`] = formData.slug;
            });
            // Tüm ürünleri tek seferde güncelle
            await update(ref(db), updates);
          }
        }
      } else {
        // Yeni Kayıt
        const newRef = push(ref(db, 'categories'));
        await set(newRef, { ...formData, id: newRef.key });
      }
      
      setIsModalOpen(false);
      setEditingId(null);
      setOriginalSlug(null);
      setFormData({ name: '', slug: '', icon: '', isVisibleOnHome: true });
    } catch (error) {
      console.error("Kategori güncelleme hatası:", error);
      alert("Hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (cat: Category) => {
    setFormData({
      name: cat.name,
      slug: cat.slug,
      icon: cat.icon,
      isVisibleOnHome: cat.isVisibleOnHome !== false
    });
    setEditingId(cat.id);
    setOriginalSlug(cat.slug); // Eski slug'ı sakla
    setIsModalOpen(true);
  };

  const deleteCategory = (id: string) => {
    if (window.confirm('Bu kategoriyi silmek istediğinize emin misiniz? Bu kategoriye bağlı ürünler etkilenmez ancak kategorisiz görünebilirler.')) {
      remove(ref(db, `categories/${id}`));
    }
  };

  const toggleHomeVisibility = async (cat: Category) => {
    const newValue = !(cat.isVisibleOnHome !== false);
    await update(ref(db, `categories/${cat.id}`), { isVisibleOnHome: newValue });
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-black text-stone-900">Kategori Yönetimi</h1>
          <p className="text-stone-500">Katalog yapısını buradan düzenleyebilir ve ana sayfa vitrinini yönetebilirsiniz.</p>
        </div>
        <button 
          onClick={() => {
            setEditingId(null);
            setOriginalSlug(null);
            setFormData({ name: '', slug: '', icon: '', isVisibleOnHome: true });
            setIsModalOpen(true);
          }}
          className="bg-amber-800 text-white px-6 py-3 rounded-2xl font-bold flex items-center space-x-2 shadow-lg shadow-amber-900/20 hover:bg-amber-900 transition-all"
        >
          <Plus size={20} />
          <span>Yeni Kategori Ekle</span>
        </button>
      </div>

      <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100 mb-8 flex items-start space-x-3">
        <Info className="text-amber-700 mt-0.5" size={18} />
        <p className="text-xs text-amber-900 leading-relaxed font-medium">
          <b>Önemli:</b> Kategori ismini veya slug'ını değiştirdiğinizde, bu kategoriye bağlı tüm ürünler otomatik olarak yeni isme taşınır. Hiçbir ürününüz kaybolmaz.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((cat) => (
          <div key={cat.id} className={`bg-white p-6 rounded-[2rem] border transition-all group ${cat.isVisibleOnHome === false ? 'border-stone-100 opacity-60' : 'border-stone-100 shadow-sm'}`}>
            <div className="flex items-center justify-between mb-6">
              <div className="w-16 h-16 bg-stone-50 rounded-2xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform">
                {cat.icon}
              </div>
              <div className="flex items-center space-x-1">
                <button 
                  onClick={() => toggleHomeVisibility(cat)}
                  className={`p-2 rounded-lg transition-all ${cat.isVisibleOnHome === false ? 'text-stone-300 hover:text-amber-800 bg-stone-50' : 'text-amber-800 bg-amber-50'}`}
                  title={cat.isVisibleOnHome === false ? "Ana Sayfada Göster" : "Ana Sayfada Gizle"}
                >
                  {cat.isVisibleOnHome === false ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
                <button 
                  onClick={() => startEdit(cat)}
                  className="p-2 text-stone-300 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="Düzenle"
                >
                  <Edit2 size={18} />
                </button>
                <button 
                  onClick={() => deleteCategory(cat.id)}
                  className="p-2 text-stone-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  title="Sil"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            <h3 className="text-xl font-bold text-stone-900 mb-1">{cat.name}</h3>
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-stone-400 font-mono">/{cat.slug}</p>
              {cat.isVisibleOnHome === false && (
                <span className="text-[9px] font-black text-stone-400 uppercase tracking-widest">Ana Sayfada Gizli</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
            <div className="p-6 border-b border-stone-100 flex items-center justify-between bg-stone-50">
              <h2 className="text-xl font-black text-stone-900">{editingId ? 'Kategoriyi Düzenle' : 'Yeni Kategori'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-stone-400 hover:text-stone-600"><X size={24} /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Kategori Adı</label>
                <input 
                  type="text" required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9ğüşıöç]/g, '-').replace(/-+/g, '-')})}
                  className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                  placeholder="Örn: Soya Mumları"
                />
              </div>
              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">URL Slug</label>
                <input 
                  type="text" required
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">İkon (Emoji)</label>
                <input 
                  type="text" required
                  value={formData.icon}
                  onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-3xl text-center text-stone-900"
                  placeholder="🏺"
                />
              </div>

              <div className="pt-4">
                <label className="flex items-center space-x-3 cursor-pointer group p-4 bg-stone-50 rounded-2xl border border-stone-100">
                   <input 
                    type="checkbox"
                    checked={formData.isVisibleOnHome}
                    onChange={e => setFormData({...formData, isVisibleOnHome: e.target.checked})}
                    className="w-5 h-5 rounded border-stone-300 text-amber-800 focus:ring-amber-500"
                   />
                   <div>
                      <p className="text-sm font-bold text-stone-900">Ana Sayfada Göster</p>
                      <p className="text-[10px] text-stone-400 uppercase font-black">Koleksiyonlar bölümünde listelenir</p>
                   </div>
                </label>
              </div>

              <div className="flex gap-4 pt-4">
                <button 
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-amber-800 text-white py-4 rounded-2xl font-bold flex items-center justify-center space-x-2 hover:bg-amber-900 transition-all shadow-xl shadow-amber-800/20 disabled:opacity-50"
                >
                  {loading ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />}
                  <span>{loading ? 'Güncelleniyor...' : (editingId ? 'Güncelle' : 'Kaydet')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
